import { useEffect } from "react";

/**
 * Restores focus to the element that opened a dialog, once the dialog is gone.
 *
 * MUI does this itself, but in the wrong order: the focus restore lives in
 * `FocusTrap`, a child of `Modal`, while the `aria-hidden` cleanup on the app
 * root lives in `Modal`. React runs child effects before parent effects, so the
 * trigger regains focus while it is still inside an `aria-hidden` subtree, and
 * Chrome blocks it with "Blocked aria-hidden on an element because its
 * descendant retained focus".
 *
 * The theme therefore sets `disableRestoreFocus` on `MuiDialog`, and this hook
 * puts focus back afterwards. It deliberately only acts when nothing else has
 * claimed focus, so a dialog that moves focus on close (or navigates away) keeps
 * whatever it chose.
 */
export const useDialogFocusRestore = (rootId = "root") => {
  useEffect(() => {
    const root = document.getElementById(rootId);
    if (!root) return;

    let lastFocused: HTMLElement | null = null;

    const handleFocusIn = (event: FocusEvent) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      // Only remember focus inside the app, never inside a dialog — the whole
      // app root is aria-hidden while one is open.
      if (root.getAttribute("aria-hidden") === "true") return;
      if (!root.contains(target)) return;
      lastFocused = target;
    };

    const restore = () => {
      // Only step in when the dialog left focus on the document body.
      const active = document.activeElement;
      if (active && active !== document.body) return;

      const target = lastFocused;
      if (!target?.isConnected) return;
      if (target.closest("[aria-hidden='true']")) return;

      target.focus({ preventScroll: true });
    };

    const observer = new MutationObserver(() => {
      // The attribute is removed once the last dialog has finished closing.
      if (root.getAttribute("aria-hidden") !== "true") restore();
    });

    document.addEventListener("focusin", handleFocusIn, true);
    observer.observe(root, {
      attributes: true,
      attributeFilter: ["aria-hidden"],
    });

    return () => {
      document.removeEventListener("focusin", handleFocusIn, true);
      observer.disconnect();
    };
  }, [rootId]);
};
